/**
 * ТЗ 0093 — реестр раздела «Настройки»: какие разделы есть, в какой группе,
 * куда ведут и КОМУ видны.
 *
 * Одна правда на четыре места: хаб `/settings` рисует карточки отсюда, меню
 * решает, показывать ли «Все настройки», палитра строит команды разделов,
 * `AppRouter` выдаёт маршруты (`ROLE_LISTED_ROUTES`). Роли — ТЕ ЖЕ
 * константы, что у маршрутов, меню и палитры соседних разделов: список,
 * переписанный литералом, разъезжается с оригиналом при первой же правке.
 *
 * Настройки — то, что конфигурируют один раз и что определяет, как работает
 * программа (решение владельца 30.09). Ежедневная работа — курсы, группы,
 * журналы, ручная блокировка, журналы отправок — живёт в своих разделах.
 *
 * Скрытие раздела — UX, а не защита: авторитет у бэкенда. Но гейт нужен и
 * чтобы не слать запрос с чужой роли — отказ рисовал бы ошибку поверх
 * пустого экрана.
 */

import {
  Bot,
  Briefcase,
  CalendarClock,
  Coins,
  LayoutTemplate,
  LockKeyhole,
  LucideIcon,
  MailCheck,
  Megaphone,
  MessageSquareText,
  PlugZap,
  Sparkles,
  UserRound,
} from 'lucide-react';

import { PATHS } from 'router/config';
import { UserRole } from 'types';

import { LEAD_ADMIN_ROLES } from '../Crm/useLeadPermissions';
import { DOFIN_READ_ROLES } from '../Dofinansowanie/useDofinPermissions';
import { MARKETING_ROLES } from '../Marketing/useMarketingPermissions';
import {
  ADMIN_ROLES,
  CAREER_ROLES,
  FINANCE_PLAN_ROLES,
  MCP_ACCESS_ROLES,
  PAYMENT_ACCESS_ROLES,
  STUDENT_ROLES,
  visibleFor,
  ZOOM_ROLES,
} from '../Palette/paletteRoles';
import { SMS_ROLES } from '../Sms/useSmsPermissions';
import { INTEGRATION_TABS, IntegrationTab } from './settingsLinks';

/**
 * Группы хаба — в порядке показа. `domains` (ТЗ 0093 этап 2а) — настройки
 * отделов: справочники маркетинга, правила и параметры финансов, тексты
 * карьерного центра.
 */
export const SETTINGS_GROUPS = [
  'automation',
  'messages',
  'domains',
  'integrations',
  'access',
] as const;
export type SettingsGroup = typeof SETTINGS_GROUPS[number];

export type SettingsSectionId =
  | 'robots'
  | 'lmsAccess'
  | 'sms'
  | 'templates'
  | 'dofinMail'
  | 'marketing'
  | 'finance'
  | 'career'
  | 'ai'
  | 'integrations'
  | 'users'
  | 'mcp';

/**
 * `section` — раздел живёт в хабе (`/settings/<раздел>`, оболочка «← Все
 * настройки»); `link` — карточка ведёт в самостоятельный раздел программы,
 * у которого свой пункт меню (пользователи, подключённые ИИ).
 */
export type SettingsSectionKind = 'section' | 'link';

/** Куда ведёт карточка: адрес из `PATHS` и, если нужно, вкладка. */
export interface ISettingsTarget {
  pathname: PATHS;
  search?: string;
}

export interface ISettingsSection {
  id: SettingsSectionId;
  group: SettingsGroup;
  /** i18n-ключ названия (хаб, оболочка раздела, палитра). */
  titleKey: string;
  /** i18n-ключ строки «что здесь настраивают». */
  descKey: string;
  icon: LucideIcon;
  to: ISettingsTarget;
  roles: UserRole[];
  kind: SettingsSectionKind;
  /** Слова для поиска в палитре (ru/pl/en), не ключи i18n. */
  keywords: string[];
}

/**
 * Вкладки «Интеграций» и их роли: Kommo правит только Admin (синхронизация,
 * сопоставление, повтор примечаний — `ADMIN_ROLES` бэкенда), Slack — роли
 * коммуникатора (там живут ящики настроек группы), аккаунты Zoom — роли
 * раздела Zoom.
 */
export const INTEGRATION_TAB_ROLES: Readonly<
  Record<IntegrationTab, UserRole[]>
> = {
  kommo: LEAD_ADMIN_ROLES,
  slack: STUDENT_ROLES,
  zoom: ZOOM_ROLES,
};

/** Вкладки «Интеграций», видимые ролям, — в порядке показа. */
export const visibleIntegrationTabs = (
  roles: readonly UserRole[],
): IntegrationTab[] =>
  INTEGRATION_TABS.filter((tab) =>
    visibleFor([...roles], INTEGRATION_TAB_ROLES[tab]),
  );

const unionRoles = (...lists: UserRole[][]): UserRole[] =>
  Array.from(new Set(lists.flat()));

/** Раздел виден, если видна хотя бы одна его вкладка. */
const INTEGRATIONS_ROLES = unionRoles(
  ...INTEGRATION_TABS.map((tab) => INTEGRATION_TAB_ROLES[tab]),
);

/**
 * ТЗ 0093 этап 2б — «Роботы и расписания» и «ИИ» (runtime-настройки).
 * Зеркало бэкенда: `SETTINGS_ROLES` (объединение ролей ключей реестра —
 * сейчас у всех ключей Admin) и `SCHEDULE_ROLES` (Admin). Появится ключ с
 * другой ролью на сервере — список расширяется здесь; какие ключи увидит
 * роль, решает сервер.
 */
export const RUNTIME_SETTINGS_ROLES: UserRole[] = [UserRole.ADMIN];

const titleKey = (id: SettingsSectionId) => `settingsHub.sections.${id}.title`;
const descKey = (id: SettingsSectionId) => `settingsHub.sections.${id}.desc`;

export const SETTINGS_SECTIONS: readonly ISettingsSection[] = [
  {
    // ТЗ 0093 этап 2б: выключатели роботов, каналов, почты, интеграций и
    // пороги (runtime-настройки без пересоздания контейнера), таблица
    // периодических задач и журнал изменений.
    id: 'robots',
    group: 'automation',
    titleKey: titleKey('robots'),
    descKey: descKey('robots'),
    icon: CalendarClock,
    to: { pathname: PATHS.settingsRobots },
    roles: RUNTIME_SETTINGS_ROLES,
    kind: 'section',
    keywords: [
      'роботы',
      'расписания',
      'периодические задачи',
      'выключатели',
      'каналы',
      'celery',
      'beat',
      'roboty',
      'harmonogramy',
      'zadania okresowe',
      'przełączniki',
      'kanały',
    ],
  },
  {
    // ТЗ 0074: не «приостановка доступа» как таковая (ручная блокировка —
    // в карточке студента и у должников), а настройки робота.
    id: 'lmsAccess',
    group: 'automation',
    titleKey: titleKey('lmsAccess'),
    descKey: descKey('lmsAccess'),
    icon: LockKeyhole,
    to: { pathname: PATHS.settingsLmsAccess },
    roles: PAYMENT_ACCESS_ROLES,
    kind: 'section',
    keywords: [
      'робот',
      'блокировка',
      'должники',
      'приостановка',
      'lms',
      'robot',
      'blokada',
      'dłużnicy',
      'wstrzymanie',
    ],
  },
  {
    // ТЗ 0093 этап 2б: модели Claude по функциям и выключатели платных
    // ИИ-функций (проверка ДЗ, профиль преподавателя, сводка маркетинга).
    id: 'ai',
    group: 'automation',
    titleKey: titleKey('ai'),
    descKey: descKey('ai'),
    icon: Sparkles,
    to: { pathname: PATHS.settingsAi },
    roles: RUNTIME_SETTINGS_ROLES,
    kind: 'section',
    keywords: [
      'ии',
      'ai',
      'модель',
      'модели',
      'claude',
      'anthropic',
      'искусственный интеллект',
      'model',
      'modele',
      'sztuczna inteligencja',
    ],
  },
  {
    id: 'sms',
    group: 'messages',
    titleKey: titleKey('sms'),
    descKey: descKey('sms'),
    icon: MessageSquareText,
    to: { pathname: PATHS.settingsSms },
    roles: SMS_ROLES,
    kind: 'section',
    keywords: [
      'sms',
      'смс',
      'smsapi',
      'лесенки',
      'напоминания',
      'адреса ссылок',
      'drabinki',
      'przypomnienia',
      'ustawienia sms',
    ],
  },
  {
    // Содержимое — шаблоны тем SMS и писем (роли SMS) плюс блок шаблонов
    // договоров, который раздел показывает сам: при включённой подписи и
    // праве читать студенческие договоры.
    id: 'templates',
    group: 'messages',
    titleKey: titleKey('templates'),
    descKey: descKey('templates'),
    icon: LayoutTemplate,
    to: { pathname: PATHS.settingsTemplates },
    roles: SMS_ROLES,
    kind: 'section',
    keywords: [
      'шаблоны',
      'шаблон',
      'тексты',
      'письма',
      'договоры',
      'templates',
      'szablony',
      'szablon',
      'treści',
    ],
  },
  {
    // ТЗ 0085 → 0093: бывшая вкладка «Правила писем» страницы дофина. Роли —
    // читатели домена (`DOFIN_READ_ROLES`); править правила и робота можно
    // тому, кому сервер ответил `canEdit` (FinanceManager — только чтение).
    id: 'dofinMail',
    group: 'messages',
    titleKey: titleKey('dofinMail'),
    descKey: descKey('dofinMail'),
    icon: MailCheck,
    to: { pathname: PATHS.settingsDofinMail },
    roles: DOFIN_READ_ROLES,
    kind: 'section',
    keywords: [
      'дофинансирование',
      'письма',
      'правила писем',
      'рассылка',
      'sendgrid',
      'dofinansowanie',
      'listy',
      'reguły listów',
      'wysyłka',
    ],
  },
  {
    // ТЗ 0088 → 0093: справочники маркетинга (бывшая вкладка «Настройки»
    // страницы). Роли — роли раздела (`MARKETING_ROLES`); править —
    // `MARKETING_EDIT_ROLES`, маршрут сигналов — `MARKETING_ADMIN_ROLES`
    // (решает сам раздел, окончательно — сервер). Источники, план и журнал
    // выгрузки — операционка, они остались на странице маркетинга.
    id: 'marketing',
    group: 'domains',
    titleKey: titleKey('marketing'),
    descKey: descKey('marketing'),
    icon: Megaphone,
    to: { pathname: PATHS.settingsMarketing },
    roles: MARKETING_ROLES,
    kind: 'section',
    keywords: [
      'маркетинг',
      'каналы',
      'правила каналов',
      'офферы',
      'семейства курсов',
      'сигналы',
      'воронки kommo',
      'marketing',
      'kanały',
      'oferty',
      'rodziny kursów',
      'sygnały',
      'lejki',
    ],
  },
  {
    // ТЗ 0023 / 0093: правила разнесения и параметры внутренних переводов
    // cash flow, параметры платёжного календаря. Роли — те же, что у
    // маршрутов cash flow и календаря (`FINANCE_PLAN_ROLES`: Admin,
    // FinanceManager).
    id: 'finance',
    group: 'domains',
    titleKey: titleKey('finance'),
    descKey: descKey('finance'),
    icon: Coins,
    to: { pathname: PATHS.settingsFinance },
    roles: FINANCE_PLAN_ROLES,
    kind: 'section',
    keywords: [
      'финансы',
      'cash flow',
      'правила разнесения',
      'внутренние переводы',
      'платёжный календарь',
      'день выплат',
      'finanse',
      'reguły',
      'przelewy wewnętrzne',
      'kalendarz płatności',
    ],
  },
  {
    // ТЗ 0039 / 0093: тексты постов для репоста. Роли — роли карьерного
    // центра (`CAREER_ROLES`); кто правит, решает сервер.
    id: 'career',
    group: 'domains',
    titleKey: titleKey('career'),
    descKey: descKey('career'),
    icon: Briefcase,
    to: { pathname: PATHS.settingsCareer },
    roles: CAREER_ROLES,
    kind: 'section',
    keywords: [
      'карьерный центр',
      'тексты постов',
      'репост',
      'linkedin',
      'centrum kariery',
      'treści postów',
      'udostępnianie',
    ],
  },
  {
    id: 'integrations',
    group: 'integrations',
    titleKey: titleKey('integrations'),
    descKey: descKey('integrations'),
    icon: PlugZap,
    to: { pathname: PATHS.settingsIntegrations },
    roles: INTEGRATIONS_ROLES,
    kind: 'section',
    keywords: [
      'интеграции',
      'kommo',
      'slack',
      'zoom',
      'аккаунты zoom',
      'integrations',
      'integracje',
      'konta zoom',
    ],
  },
  {
    id: 'users',
    group: 'access',
    titleKey: titleKey('users'),
    descKey: descKey('users'),
    icon: UserRound,
    to: { pathname: PATHS.users },
    roles: ADMIN_ROLES,
    kind: 'link',
    keywords: ['пользователи', 'роли', 'użytkownicy', 'role', 'users'],
  },
  {
    id: 'mcp',
    group: 'access',
    titleKey: titleKey('mcp'),
    descKey: descKey('mcp'),
    icon: Bot,
    to: { pathname: PATHS.mcpAccess },
    roles: MCP_ACCESS_ROLES,
    kind: 'link',
    keywords: ['mcp', 'ии', 'ai', 'подключённые ии', 'podłączone ai'],
  },
];

/**
 * Разделы, видимые ролям, — в порядке реестра. Флагов сервера у разделов нет:
 * блок шаблонов договоров «Шаблонов сообщений» прячет себя сам, когда
 * подпись выключена, а раздел остаётся (в нём шаблоны SMS и писем).
 */
export const visibleSettingsSections = (
  roles: readonly UserRole[],
): ISettingsSection[] =>
  SETTINGS_SECTIONS.filter((section) => visibleFor([...roles], section.roles));

/**
 * Кому открыт хаб `/settings` — объединение ролей всех разделов.
 */
export const SETTINGS_HUB_ROLES: UserRole[] = unionRoles(
  ...SETTINGS_SECTIONS.map((section) => section.roles),
);

export const getSettingsSection = (id: SettingsSectionId): ISettingsSection =>
  SETTINGS_SECTIONS.find((section) => section.id === id) as ISettingsSection;

/**
 * Откроется ли роли раздел (а для «Интеграций» — именно эта вкладка). Для
 * кнопок «Настройки …» на рабочих страницах: ссылка в раздел, которого роль
 * не видит, кончилась бы возвратом в хаб. У «Интеграций» вкладки уже
 * раздела (Kommo — только Admin), поэтому решает роль вкладки.
 */
export const canOpenSettingsSection = (
  roles: readonly UserRole[],
  id: SettingsSectionId,
  integrationTab?: IntegrationTab,
): boolean =>
  visibleFor(
    [...roles],
    id === 'integrations' && integrationTab
      ? INTEGRATION_TAB_ROLES[integrationTab]
      : getSettingsSection(id).roles,
  );

/** Роли раздела — для маршрута в `AppRouter` (`ROLE_LISTED_ROUTES`). */
export const settingsSectionRoles = (id: SettingsSectionId): UserRole[] =>
  getSettingsSection(id).roles;

/** Id команды палитры для раздела: `settingsSms`, `settingsTemplates`… */
export const settingsCommandId = (id: SettingsSectionId): string =>
  `settings${id.charAt(0).toUpperCase()}${id.slice(1)}`;
